import React from "react";
import classNames from "classnames";
import { withRouter, Link } from "react-router-dom";
import { parse, format, addHours } from "date-fns";
import { unstable_useMediaQuery as useMediaQuery } from "@material-ui/core/useMediaQuery";
import { withStyles, withTheme } from "@material-ui/core/styles";
import { Helmet } from "react-helmet";
import createStyles from "@material-ui/core/styles/createStyles";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import RefreshIcon from "@material-ui/icons/Refresh";
import withContext from "../../tools/withContext";
import EnhancedTableHeader from "../common/EnhancedTableHeader";
import TableBody from "../common/TableBody";
import ProjectActionButton from "../projects/components/ProjectActionButton";
import UiProgress from "../common/UiProgess";
import UiButton from "../common/UiButton";
import ProjectType from "../../types/Project";
import ContextType from "../../types/Context";
import UserType from "../../types/User";
import DimensionsContainer from "../common/DimensionsContainer";
import {
  tablePageStyles,
  tablePageRootStyles,
  tablePageToolbarStyles,
  tableToolbarOutlineButtonStyle,
} from "../common/tableTheme";

const columns = [
  {
    id: "name",
    numeric: false,
    sortable: false,
    disablePadding: true,
    label: "Nom du projet",
    onlyRoles: null,
    size: 2,
    align: "left",
  },
  {
    id: "labelingType",
    numeric: false,
    sortable: false,
    disablePadding: false,
    label: "Type de labellisation",
    onlyRoles: null,
    size: 3,
    align: "center",
  },
  {
    id: "operationType",
    numeric: false,
    sortable: false,
    disablePadding: false,
    label: "Opération",
    onlyRoles: null,
    size: 3,
    align: "center",
  },
  {
    id: "createdAt",
    numeric: false,
    sortable: false,
    disablePadding: false,
    label: "Lancée le",
    onlyRoles: null,
    size: 2,
    align: "center",
  },
  {
    id: "secondsToFinish",
    numeric: true,
    sortable: false,
    disablePadding: false,
    label: "Lots",
    onlyRoles: null,
    size: 3,
    align: "center",
  },
  {
    id: "actions",
    numeric: false,
    sortable: false,
    disablePadding: false,
    label: "Actions",
    onlyRoles: null,
    size: 1,
    align: "right",
  },
];

const styles = (theme: Theme) =>
  createStyles({
    root: {
      ...tablePageRootStyles(theme),
    },
    ...tablePageStyles(theme),
    ...tablePageToolbarStyles(theme),
    refreshButtonModern: tableToolbarOutlineButtonStyle(),
    refreshIconModern: {
      fontSize: 18,
    },
    progress: {
      padding: `0 ${theme.spacing.unit * 2}px`,
    },
    fullWidth: {
      width: "100%",
    },
  });

interface PropsType {
  classes: any;
  ctx: ContextType;
  progress: any[];
  deleteProject: Function;
  users: UserType[];
  isLoading: boolean;
  theme: any;
  pagination: any;
  history: any;
  match: any;
  appFetch: any;
  searchTerm: string;
  setSearchTerm: Function;
  onRefresh: Function;
}

interface ProgressType {
  operationType: "UPLOAD_FILES" | "CREATE_BATCH";
  status: "FINISHED" | "STARTED";
  nbDone: number;
  nbTotal: number;
  secondsToFinish: number;
  createdAt: string;
  updatedAt: string;
  messageError: null | string;
}

interface ProjectWithProgressType extends ProjectType {
  progress: ProgressType;
}

const ProjectsOperations = (props: PropsType) => {
  const {
    classes,
    progress,
    isLoading,
    onRefresh,
    match: {
      params: { noPage },
    },
    ctx: { user },
    theme,
  } = props;
  const matches = useMediaQuery(theme.breakpoints.down("lg"));

  const buildColumns = (progressData: any) => {
    let operation =
      progressData.operationType === "CREATE_BATCH"
        ? "Génération des lots : "
        : progressData.project.labelingType === "VIDEO"
        ? "Importation des vidéos :"
        : "Importation des images :";

    return [
      <div>{progressData.project.name}</div>,
      <div>{progressData.project.labelingType}</div>,
      <div>{progressData.operationType}</div>,
      <div>
        {format(addHours(parse(progressData.createdAt), 2), "DD/MM à HH:mm:ss")}
      </div>,
      <div>
        {progressData.status !== "FINISHED" && (
          <div className={classes.progress}>
            <UiProgress
              variant="determinate"
              color="secondary"
              value={(progressData.nbDone * 100) / progressData.nbTotal}
            />
            <small style={{ display: "block", textAlign: "left" }}>
              {operation} {progressData.nbDone}/{progressData.nbTotal}{" "}
              {progressData.project.labelingType === "VIDEO"
                ? "vidéos"
                : "images"}
            </small>
          </div>
        )}
        {((progressData.operationType === "CREATE_BATCH" &&
          progressData.status === "FINISHED" &&
          progressData.project.totalBatches > 0) ||
          (progressData.operationType === "IMPORT_FROM_EXISTING" &&
            progressData.status === "FINISHED" &&
            progressData.project.dataSource === "LABELS")) && (
          <Link
            to={`/batches/0?projectId=${progressData.project.id}&sort=createdDate,desc`}
          >
            Afficher les lots
          </Link>
        )}
        {progressData.operationType !== "CREATE_BATCH" &&
          progressData.status === "FINISHED" &&
          progressData.project.dataSource !== "LABELS" && (
            <span>{`${operation} terminée`}</span>
          )}
      </div>,
      <div>
        <ProjectActionButton
          project={progressData.project}
          isAdmin={isAdmin}
          variant="modern"
          showBatches={
            progressData.project.dataSource === "LABELS" &&
            progressData.status === "FINISHED"
          }
        />
      </div>,
    ];
  };

  const isAdmin = user && user.authorities[0] === "ROLE_ADMIN";

  return (
    <Grid container direction="column" className={classes.root}>
      <Helmet>
        <title>Importations récentes</title>
      </Helmet>
      <Grid item className={classes.fullWidth}>
        <div className={classes.pageToolbar}>
          <div className={classes.pageToolbarLeft}>
            <div className={classes.pageTitleRow}>
              <Typography
                component="h2"
                className={classNames(classes.pageTitle, {
                  [classes.pageTitleCompact]: matches,
                })}
              >
                Liste des opérations récentes
              </Typography>
            </div>
          </div>
          <div className={classes.pageToolbarRight}>
            <div className={classes.pageLoaderSlot}>
              {isLoading && (
                <CircularProgress
                  color="secondary"
                  size={20}
                  className={classes.pageLoader}
                />
              )}
            </div>
            <UiButton
              type="button"
              variant="outlined"
              color="default"
              onClick={onRefresh}
              disabled={isLoading}
              className={classes.refreshButtonModern}
            >
              <RefreshIcon fontSize="small" className={classes.refreshIconModern} />
              Actualiser
            </UiButton>
          </div>
        </div>
      </Grid>
      <Grid item className={classes.tableWrapper}>
        <Paper elevation={0} className={classes.tableCard}>
          <EnhancedTableHeader
            variant="modern"
            rowCount={progress.length}
            columns={columns}
            withCheck={false}
            actionsCellSize={3}
          />
          {progress.length === 0 && !isLoading && (
            <Typography className={classes.tableEmptyInCard} variant="h6">
              Aucune opération récente
            </Typography>
          )}
          {progress.length > 0 && (
            <div className={classes.tableScrollArea}>
              <DimensionsContainer variant="modern">
                <TableBody
                  variant="modern"
                  selected={[]}
                  handleSelection={null}
                  buildColumns={buildColumns}
                  datas={progress}
                  columns={columns}
                  noPage={noPage}
                />
              </DimensionsContainer>
            </div>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default withStyles(styles)(
  withRouter(withContext(withTheme()(ProjectsOperations))),
);
